<template>
  <Col3>
    <TenderType
      :fieldName="tenderTypeField"
      :index="index"
      :plan-type="planType"
      :isDisabled="isDisabled"
    />
  </Col3>

  <Col3>
    <TenderName
      :field-name="tenderNameField"
      :index="index"
      :plan-type="planType"
      :isDisabled="isDisabled"
    />
  </Col3>

  <Col2 v-if="!showTenderSetup(index)">
    <TenderID
      :fieldName="tenderIdField"
      :index="index"
      :plan-type="planType"
      :isDisabled="isDisabled"
    />
  </Col2>

  <Col2 v-if="showTenderSetup(index)">
    <div class="flex align-items-end h-full" v-if="!isDisabled">
      <Button
        class="w-full"
        outlined
        @click="displayTenderSetupDialog"
        :label="translate(`${LOCALE_PREFIX}.form.tenders.tenderSetupButton`)"
      />

      <TenderSetupDialog
        v-model="showTenderSetupDialog"
        :parentIndex="index"
        :field="field"
        :key="index"
        @save="updateTenderValues"
      />
    </div>
  </Col2>
</template>

<script setup lang="ts">
import { useTranslation } from '@centric-os/helpers';
import { SitesLocalePrefix } from '../../../../../enums';
import { inject, ref, type PropType } from 'vue';
import TenderType from '../tender-type/TenderType.vue';
import TenderSetupDialog from '../tender-setup-dialog/TenderSetupDialog.vue';
import { cloneDeep, isEmpty, isNil } from 'lodash-es';
import { TENDER_TYPES } from '../../enums/enums';
import { useFieldArray, useFieldValue, type FieldEntry } from 'vee-validate';
import type { Tender } from '@centric-os/types';
import TenderID from '../tender-id/TenderID.vue';
import TenderName from '../tender-name/TenderName.vue';

// Define props
const props = defineProps({
  tenderIdField: {
    type: String,
    required: true,
  },
  tenderTypeField: {
    type: String,
    required: true,
  },
  tenderNameField: {
    type: String,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  field: {
    type: Object as PropType<FieldEntry<Tender>>,
    required: true,
  },
  planType: {
    type: String,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_MEAL_PLAN;

// Set tender values
const formNamespace = inject('formNamespace', '');
const tenders = useFieldValue<Tender[]>(`${formNamespace}tenders`);
const { update } = useFieldArray<Tender>(`${formNamespace}tenders`);

// Ref for showing tender setup dialog
const showTenderSetupDialog = ref<boolean>(false);

// Method to display tender setup dialog
const displayTenderSetupDialog = (): void => {
  showTenderSetupDialog.value = true;
};

// Check if tender setup should be shown
const showTenderSetup = (index: number): boolean => {
  if (isEmpty(tenders.value)) {
    return false;
  }

  const tender = tenders?.value[index];

  if (isNil(tender)) {
    return false;
  }

  if (tender.type === TENDER_TYPES.MEAL_EQUIVALENT) {
    return true;
  }

  return false;
};

// Method to update tender values from setup
const updateTenderValues = (values: Partial<Tender>): void => {
  const updatedTender = cloneDeep(props.field.value);
  updatedTender.id = values.id;
  // @ts-expect-error FIXME: Property 'tax_exempt' does not exist on type 'Tender'.
  updatedTender.tax_exempt = values.tax_exempt;
  // @ts-expect-error FIXME: Property 'max_swipes_per_transaction' does not exist on type 'Tender'.
  updatedTender.max_swipes_per_transaction = values.max_swipes_per_transaction;
  // @ts-expect-error FIXME: Property 'rates' does not exist on type 'Tender'.
  updatedTender.rates = values.rates;
  update(props.index, updatedTender);
};
</script>
